"use client";

import * as React from "react";
import { PlaceAutocompleteResult } from "@googlemaps/google-maps-services-js";
import { Command as CommandPrimitive } from "cmdk";
import { Loader2 } from "lucide-react";

import { autocomplete, getPlaceDetails } from "@/lib/google";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

interface VenueSearchProps {
  onVenueSelect: (venueData: {
    venue: string;
    address: string;
    city: string;
    state: string;
    latitude: string;
    longitude: string;
  }) => void;
  initialValue?: string;
}

export default function VenueSearch({
  onVenueSelect,
  initialValue,
}: VenueSearchProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(initialValue || "");
  const [predictions, setPredictions] = React.useState<
    PlaceAutocompleteResult[]
  >([]);
  const [isLoading, setIsLoading] = React.useState(false);

  // Track if the input should have focus
  const shouldMaintainFocus = React.useRef(false);

  React.useEffect(() => {
    if (initialValue) {
      setInputValue(initialValue);
    }
  }, [initialValue]);

  // Effect to maintain focus when needed
  React.useEffect(() => {
    if (shouldMaintainFocus.current && inputRef.current && !isLoading) {
      inputRef.current.focus();
      shouldMaintainFocus.current = false;
    }
  }, [isLoading, predictions]);

  React.useEffect(() => {
    const fetchPredictions = async () => {
      if (inputValue.length > 1) {
        // Set flag to maintain focus after API call completes
        shouldMaintainFocus.current =
          document.activeElement === inputRef.current;

        setIsLoading(true);
        try {
          const results = await autocomplete(inputValue);
          setPredictions(results ?? []);
        } catch (error) {
          console.error("Error fetching predictions:", error);
          setPredictions([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setPredictions([]);
      }
    };

    const debounceTimeout = setTimeout(fetchPredictions, 300);
    return () => clearTimeout(debounceTimeout);
  }, [inputValue]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (input) {
        if (e.key === "Escape") {
          input.blur();
        }
      }
    },
    [],
  );

  const handleVenueSelect = async (prediction: PlaceAutocompleteResult) => {
    shouldMaintainFocus.current = false; // Don't maintain focus after selection
    setIsLoading(true);

    try {
      const placeDetails = await getPlaceDetails(prediction.place_id);
      const location = placeDetails?.geometry?.location;

      const mainText = prediction.structured_formatting.main_text;
      const secondaryText = prediction.structured_formatting.secondary_text;

      // Improved city and state extraction
      const addressParts = secondaryText.split(", ");
      const city = addressParts[addressParts.length - 3] || "";
      const stateAndCountry = addressParts[addressParts.length - 2] || "";
      const state = stateAndCountry.split(" ")[0] || "";

      onVenueSelect({
        venue: mainText,
        address: prediction.description,
        city,
        state,
        latitude: location?.lat.toString() || "",
        longitude: location?.lng.toString() || "",
      });

      setInputValue(mainText);
      setOpen(false);
    } catch (error) {
      console.error("Error getting place details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Command
      onKeyDown={handleKeyDown}
      className="h-fit overflow-visible bg-transparent"
    >
      <div className="flex h-9 w-full rounded-md border border-zinc-200 bg-transparent px-3 py-1 text-sm shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-zinc-500 focus-visible:ring-1 focus-visible:ring-zinc-950 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-800 dark:placeholder:text-zinc-400 dark:focus-visible:ring-zinc-300">
        <div className="relative flex w-full items-center">
          <CommandPrimitive.Input
            ref={inputRef}
            value={inputValue}
            onValueChange={setInputValue}
            onBlur={() => {
              // Small delay to allow clicking on results
              setTimeout(() => setOpen(false), 200);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Search for a venue..."
            className="placeholder:text-muted-foreground w-full flex-1 bg-transparent pr-6 outline-hidden"
          />
          {isLoading && (
            <div className="absolute right-0 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
            </div>
          )}
        </div>
      </div>
      <div className="relative">
        <CommandList>
          {open && predictions.length > 0 ? (
            <div className="bg-popover text-popover-foreground animate-in absolute top-0 z-10 w-full rounded-md border shadow-md outline-hidden">
              <CommandGroup className="overflow-auto">
                {predictions.map((prediction) => (
                  <CommandItem
                    key={prediction.place_id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onSelect={() => handleVenueSelect(prediction)}
                    className="text-xxs cursor-pointer"
                  >
                    {prediction.description}
                  </CommandItem>
                ))}
              </CommandGroup>
            </div>
          ) : null}
        </CommandList>
      </div>
    </Command>
  );
}
